import React, { FC, useState } from 'react';
import {
    DialogContent,
    Box,
    Grid,
    TextField,
    DialogActions,
    Button,
    FormControl,
    FormControlLabel,
    Switch,
    InputAdornment,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Paper,
    FormGroup,
    FormLabel,
    Checkbox,
} from '@mui/material';
import { LabAction } from 'models/action';
import { useMutation } from '@apollo/client';
import { modifyActionTemplate } from 'apollo/templates/action';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import NumberOnlyTextField from 'components/number-only-text-field/number-only-text-field';
import { usePlanningTemplate } from 'hooks/usePlanningTemplate';
import { useValueTemplate } from 'hooks/useValueTemplate';
import { RestorationType } from 'models/order';
import RestorationTypeAutocomplete from 'components/shared/restoration-type-autocomplete/restoration-type-autocomplete.component';
import InternalCodeAutocomplete from 'components/lab-settings/action-settings/shared/internal-code-autocomplete.component';
import MaterialMacroMultiSelect from 'components/lab-settings/action-settings/shared/material-macro-multi-select.component';

type TemplateRef = { digitalTemplateId: string | null; physicalTemplateId: string | null };
type TemplateOption = { _id: string; name: string };

type EditActionFormProps = {
    formId: string;
    onCloseDialog: () => void;
    categories: { _id: string; name: string }[];
    groups: { _id: string; name: string }[];
    actionToEdit: LabAction;
    onEditAction: () => void;
};
const EditActionForm: FC<EditActionFormProps> = ({
    formId,
    onCloseDialog,
    categories,
    groups,
    actionToEdit,
    onEditAction,
}: EditActionFormProps) => {
    const { t: translate } = useTranslation(['common', 'actions-materials', 'workflow', 'orders', 'lab-settings']);
    const [selectedCategory, setSelectedCategory] = useState(actionToEdit.category);
    const [selectedGroups, setSelectedGroups] = useState<string[]>(
        actionToEdit.groups.filter(Boolean).map((group) => group._id),
    );
    const [selectedMaterialMacros, setSelectedMaterialMacros] = useState<string[]>(
        (actionToEdit.materialMacros || []).map((m) => m._id as string),
    );
    const [planningTemplate, setPlanningTemplate] = useState<TemplateRef>({
        digitalTemplateId: actionToEdit?.productionPlanningTemplate?.digitalTemplateId || null,
        physicalTemplateId: actionToEdit?.productionPlanningTemplate?.physicalTemplateId || null,
    });
    const [valueTemplate, setValueTemplate] = useState<TemplateRef>({
        digitalTemplateId: actionToEdit?.productionValueTemplate?.digitalTemplateId || null,
        physicalTemplateId: actionToEdit?.productionValueTemplate?.physicalTemplateId || null,
    });
    const { showBoundary } = useDenthubErrorBoundary();
    const { planningTemplates } = usePlanningTemplate();
    const { valueTemplates } = useValueTemplate();

    const [modifyAction] = useMutation(modifyActionTemplate, {
        onCompleted: ({ modifyAction }) => {
            if (modifyAction) {
                onEditAction();
            }
        },
        onError: (error) => {
            showBoundary(error);
        },
    });

    const validate = (values: LabAction) => {
        const errors: Record<string, string> = {};
        if (!values.actionCode) {
            errors.actionCode = translate('actions-materials:VALIDATION:ACTION_CODE');
        }
        if (!values.description) {
            errors.description = translate('workflow:VALIDATION:DESCRIPTION');
        }
        if (values.productionDeductionValue !== undefined && values.productionDeductionValue !== null) {
            const value = Number(values.productionDeductionValue);
            if (value < 0) {
                errors.productionDeductionValue = translate('actions-materials:PRODUCTION_DEDUCTION_VALUE_POSITIVE');
            } else if (!Number.isInteger(value)) {
                errors.productionDeductionValue = translate('actions-materials:PRODUCTION_DEDUCTION_VALUE_INTEGER');
            }
        }
        return errors;
    };

    const formik = useFormik({
        initialValues: {
            ...actionToEdit,
            internalCode: actionToEdit.internalCode || '',
        },
        validate,
        enableReinitialize: true,
        onSubmit: async (values) => {
            const category = selectedCategory;
            const vat = parseFloat(values.vat?.toString() || '');
            const productionDeductionValue = values.productionDeductionValue
                ? parseInt(values.productionDeductionValue?.toString())
                : null;
            await modifyAction({
                variables: {
                    ...values,
                    category: category,
                    groups: selectedGroups,
                    materialMacros: selectedMaterialMacros,
                    vat: vat || 0,
                    productionPlanningTemplate: planningTemplate,
                    productionValueTemplate: valueTemplate,
                    productionDeductionValue,
                },
            });
            onCloseDialog();
        },
    });

    const onCategoryChange = (event: SelectChangeEvent<string>) => {
        const category = event.target.value;
        setSelectedCategory(category);
    };

    const onGroupsChange = (groupId: string, isChecked: boolean) => {
        if (isChecked) {
            setSelectedGroups([...selectedGroups, groupId]);
        } else {
            setSelectedGroups(selectedGroups.filter((g) => g !== groupId));
        }
    };

    const handlePlanningTemplateChange = (event: SelectChangeEvent, field: keyof TemplateRef) => {
        const value = event.target.value || null;
        setPlanningTemplate((prev) => ({ ...prev, [field]: value }));
    };

    const handleValueTemplateChange = (event: SelectChangeEvent, field: keyof TemplateRef) => {
        const value = event.target.value || null;
        setValueTemplate((prev) => ({ ...prev, [field]: value }));
        if (!value) {
            formik.setFieldValue('productionDeductionValue', undefined);
        }
    };

    const handleDenthubRestorationTypeChange = (denthubRestorationType: RestorationType) => {
        formik.setFieldValue('denthubRestorationType', denthubRestorationType);
    };

    const hasAnyTemplate =
        planningTemplate.digitalTemplateId ||
        planningTemplate.physicalTemplateId ||
        valueTemplate.digitalTemplateId ||
        valueTemplate.physicalTemplateId;

    return (
        <form id={formId} onSubmit={formik.handleSubmit}>
            <Box>
                <Grid container spacing={1}>
                    <Grid item sm={12}>
                        <FormControl margin="dense" fullWidth>
                            <InputLabel required>{translate('orders:CATEGORY')}</InputLabel>
                            <Select
                                value={selectedCategory}
                                label={translate('orders:CATEGORY')}
                                onChange={onCategoryChange}
                            >
                                {categories.map((category, index) => (
                                    <MenuItem key={index} value={category.name}>
                                        {category.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item sm={12}>
                        <FormControl fullWidth>
                            <RestorationTypeAutocomplete
                                value={formik.values.denthubRestorationType as RestorationType | null}
                                onChange={handleDenthubRestorationTypeChange}
                                error={
                                    formik.touched.denthubRestorationType &&
                                    Boolean(formik.errors.denthubRestorationType)
                                }
                                helperText={
                                    formik.touched.denthubRestorationType && formik.errors.denthubRestorationType
                                }
                            />
                        </FormControl>
                    </Grid>
                    <Grid item md={6} sm={12}>
                        <FormControl fullWidth>
                            <TextField
                                id="action-code"
                                margin="dense"
                                label={translate('actions-materials:CODE')}
                                required
                                helperText={formik.touched.actionCode && formik.errors.actionCode}
                                error={formik.touched.actionCode && Boolean(formik.errors.actionCode)}
                                {...formik.getFieldProps('actionCode')}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item md={6} sm={12}>
                        <FormControl fullWidth>
                            <NumberOnlyTextField
                                id="vat"
                                label={translate('orders:VAT')}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                                {...formik.getFieldProps('vat')}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item md={6} sm={12}>
                        <FormControl fullWidth>
                            <InternalCodeAutocomplete
                                value={formik.values.internalCode || ''}
                                onChange={(internalCode) => formik.setFieldValue('internalCode', internalCode)}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item md={6} sm={12}>
                        <FormControl fullWidth>
                            <TextField
                                id="description"
                                margin="dense"
                                label={translate('common:DESCRIPTION')}
                                required
                                helperText={formik.touched.description && formik.errors.description}
                                error={formik.touched.description && Boolean(formik.errors.description)}
                                {...formik.getFieldProps('description')}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item sm={12}>
                        <Paper variant="outlined">
                            <Box p={2}>
                                <FormControl component="fieldset" variant="standard">
                                    <FormLabel component="legend">{translate('lab-settings:GROUPS')}</FormLabel>
                                    <FormGroup row>
                                        {groups.map((group) => (
                                            <FormControlLabel
                                                key={group._id}
                                                control={
                                                    <Checkbox
                                                        checked={selectedGroups.includes(group._id)}
                                                        onChange={(event) =>
                                                            onGroupsChange(group._id, event.target.checked)
                                                        }
                                                        name={group._id}
                                                    />
                                                }
                                                label={group.name}
                                            />
                                        ))}
                                    </FormGroup>
                                </FormControl>
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item sm={12}>
                        <FormControl fullWidth>
                            <MaterialMacroMultiSelect
                                selectedMaterialMacroIds={selectedMaterialMacros}
                                onChange={setSelectedMaterialMacros}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item sm={6}>
                        <FormControl margin="dense" fullWidth>
                            <InputLabel>{translate('lab-settings:DIGITAL_PLAN')}</InputLabel>
                            <Select
                                label={translate('lab-settings:DIGITAL_PLAN')}
                                value={planningTemplate.digitalTemplateId || ''}
                                onChange={(event) => handlePlanningTemplateChange(event, 'digitalTemplateId')}
                            >
                                <MenuItem value="">
                                    <em>{translate('actions-materials:NO_PRODUCTION_PLAN')}</em>
                                </MenuItem>
                                {(planningTemplates as TemplateOption[]).map((t) => (
                                    <MenuItem key={t._id} value={t._id}>
                                        {t.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item sm={6}>
                        <FormControl margin="dense" fullWidth>
                            <InputLabel>{translate('lab-settings:PHYSICAL_PLAN')}</InputLabel>
                            <Select
                                label={translate('lab-settings:PHYSICAL_PLAN')}
                                value={planningTemplate.physicalTemplateId || ''}
                                onChange={(event) => handlePlanningTemplateChange(event, 'physicalTemplateId')}
                            >
                                <MenuItem value="">
                                    <em>{translate('actions-materials:NO_PRODUCTION_PLAN')}</em>
                                </MenuItem>
                                {(planningTemplates as TemplateOption[]).map((t) => (
                                    <MenuItem key={t._id} value={t._id}>
                                        {t.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item sm={6}>
                        <FormControl margin="dense" fullWidth>
                            <InputLabel>{translate('lab-settings:DIGITAL_VALUE_TEMPLATE')}</InputLabel>
                            <Select
                                label={translate('lab-settings:DIGITAL_VALUE_TEMPLATE')}
                                value={valueTemplate.digitalTemplateId || ''}
                                onChange={(event) => handleValueTemplateChange(event, 'digitalTemplateId')}
                            >
                                <MenuItem value="">
                                    <em>{translate('actions-materials:NO_VALUE_TEMPLATE')}</em>
                                </MenuItem>
                                {(valueTemplates as TemplateOption[]).map((t) => (
                                    <MenuItem key={t._id} value={t._id}>
                                        {t.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item sm={6}>
                        <FormControl margin="dense" fullWidth>
                            <InputLabel>{translate('lab-settings:PHYSICAL_VALUE_TEMPLATE')}</InputLabel>
                            <Select
                                label={translate('lab-settings:PHYSICAL_VALUE_TEMPLATE')}
                                value={valueTemplate.physicalTemplateId || ''}
                                onChange={(event) => handleValueTemplateChange(event, 'physicalTemplateId')}
                            >
                                <MenuItem value="">
                                    <em>{translate('actions-materials:NO_VALUE_TEMPLATE')}</em>
                                </MenuItem>
                                {(valueTemplates as TemplateOption[]).map((t) => (
                                    <MenuItem key={t._id} value={t._id}>
                                        {t.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item sm={6}>
                        <FormControl fullWidth>
                            <TextField
                                id="production-deduction-value"
                                margin="dense"
                                type="number"
                                inputProps={{ min: 0, step: 1 }}
                                label={translate('actions-materials:PRODUCTION_DEDUCTION_VALUE')}
                                helperText={
                                    formik.touched.productionDeductionValue && formik.errors.productionDeductionValue
                                }
                                error={
                                    formik.touched.productionDeductionValue &&
                                    Boolean(formik.errors.productionDeductionValue)
                                }
                                {...formik.getFieldProps('productionDeductionValue')}
                                disabled={!hasAnyTemplate}
                                value={
                                    formik.values.productionDeductionValue === undefined
                                        ? ''
                                        : formik.values.productionDeductionValue
                                }
                            />
                        </FormControl>
                    </Grid>

                    <Grid item sm={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    id="inactive"
                                    name="inactive"
                                    checked={!formik.values.inactive}
                                    onChange={(event) => formik.setFieldValue('inactive', !event.target.checked)}
                                />
                            }
                            label={!formik.values.inactive ? translate('common:ACTIVE') : translate('common:INACTIVE')}
                        />
                    </Grid>
                </Grid>
            </Box>
        </form>
    );
};

export default EditActionForm;
